import React, { useContext, createContext, useState, useCallback } from 'react';
import { uuid } from 'uuidv4';

import api from '../services/api';

import { useToast } from './toast';
import { useAuth } from './auth';

type Tamanho = {
  id: string | number;
  descricao: string;
  numberOfSlices: number;
  numberOfFlavors: number;
  isNew: boolean;
  ordem: number;
};

export type ItemComplemento = {
  id: string | number;
  descricao: string;
  imagem?: File;
  preco: number;
  empresa_id: number;
  codigo_complemento?: string;
  qtd_min: number;
  tipo: number;
  qtd_max: number;
  produto_id?: number;
  index_complemento: number;
  isNew: boolean;
};

export type Complemento = {
  id?: number;
  index: number;
  descricao: string;
  complemento_obrigatorio: boolean;
  tipo: number;
  qtd_min: number;
  qtd_max: number;
  empresa_id: number;
  isNew: boolean;
  ordem?: number;
  ativo?: boolean;
  pausar_venda?: boolean;
};

type GrupoPizzaData = {
  descricao: string;
  image?: File | string;
  pausar_venda: boolean;
  tamanhos: Tamanho[];
  complementos: Complemento[];
  itens_complemento: ItemComplemento[];
};

interface GrupoPizzaContextData {
  grupoPizzaId?: number;
  drawerIsOpen: boolean;
  currentTabIndex: number;
  tamanhosIsEnabled: boolean;
  complementosIsEnabled: boolean;
  grupoPizzaData: GrupoPizzaData;
  selectedComplementoIndex: number;
  complementosDrawerIsOpen: boolean;
  currentComplementosTabIndex: number;
  itensComplementosIsEnabled: boolean;

  openDrawer(): void;
  resetDrawer(): void;
  enableTamanhos(): void;
  enableComplementos(): void;
  resetComplementosTab(): void;
  enableItensComplementos(): void;
  toggleComplementosDrawer(): void;
  updateGrupoPizza(): Promise<void>;
  createNewGrupoPizza(): Promise<void>;
  toggleEditingGrupo(id: number): void;
  deleteComplemento(index: number): void;
  changeCurrentTabIndex(tabIndex: number): void;
  changeGrupoPizzaData(grupoPizzaData: any): void;
  changeSelectedComplementoIndex(index: number): void;
  changeCurrentComplementosTabIndex(tabIndex: number): void;
}

const GrupoPizzaContext = createContext<GrupoPizzaContextData>(
  {} as GrupoPizzaContextData,
);

const GrupoPizzaProvider: React.FC = ({ children }) => {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [grupoPizzaId, setGrupoPizzaId] = useState<undefined | number>();
  const [complementosDrawerIsOpen, setComplementosDrawerIsOpen] = useState(
    false,
  );
  const [
    currentComplementosTabIndex,
    setCurrentComplementosTabIndex,
  ] = useState(0);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [selectedComplementoIndex, setSelectedComplementoIndex] = useState(0);
  const [grupoPizzaData, setGrupoPizzaData] = useState<GrupoPizzaData>({
    tamanhos: [
      {
        id: uuid(),
        descricao: 'Pequena',
        numberOfFlavors: 1,
        numberOfSlices: 1,
      },
      {
        id: uuid(),
        descricao: 'Média',
        numberOfFlavors: 2,
        numberOfSlices: 6,
      },
      {
        id: uuid(),
        descricao: 'Grande',
        numberOfFlavors: 3,
        numberOfSlices: 8,
      },
    ],
    complementos: [],
    itens_complemento: [],
  } as any);
  const [tamanhosIsEnabled, setTamanhosIsEnabled] = useState(false);
  const [itensComplementosIsEnabled, setItensComplementosIsEnabled] = useState(
    false,
  );
  const [complementosIsEnabled, setComplementosIsEnabled] = useState(false);

  const changeCurrentTabIndex = useCallback((tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
  }, []);

  const resetComplementosTab = useCallback(() => {
    setComplementosDrawerIsOpen(false);
    setCurrentComplementosTabIndex(0);
    setItensComplementosIsEnabled(false);
  }, []);

  const changeGrupoPizzaData = useCallback(
    (grupoPizzaPartialData: any) => {
      setGrupoPizzaData({ ...grupoPizzaData, ...grupoPizzaPartialData });
    },
    [grupoPizzaData],
  );

  const changeSelectedComplementoIndex = useCallback((index: number) => {
    setSelectedComplementoIndex(index);
  }, []);

  const enableTamanhos = useCallback(() => {
    setTamanhosIsEnabled(true);
  }, []);

  const enableComplementos = useCallback(() => {
    setComplementosIsEnabled(true);
  }, []);

  const openDrawer = useCallback(() => {
    setDrawerIsOpen(true);
  }, []);

  const deleteComplemento = useCallback(
    async (index: number) => {
      const findComplemento = grupoPizzaData.complementos.find(
        (complemento) => complemento.index === index,
      );

      if (findComplemento) {
        const complementos = grupoPizzaData.complementos.filter(
          (complemento) => complemento.index !== index,
        );
        const itens_complemento = grupoPizzaData.itens_complemento.filter(
          (item) => item.index_complemento !== index,
        );

        setGrupoPizzaData({
          ...grupoPizzaData,
          complementos,
          itens_complemento,
        });

        if (typeof findComplemento.id === 'number') {
          await api.post(`/complemento/${findComplemento.id}`);
        }
      }
    },
    [grupoPizzaData],
  );

  const resetDrawer = useCallback(() => {
    setDrawerIsOpen(false);
    setCurrentTabIndex(0);
    setSelectedComplementoIndex(0);
    setGrupoPizzaData({
      tamanhos: [
        {
          id: uuid(),
          descricao: 'Pequena',
          numberOfFlavors: 1,
          numberOfSlices: 1,
        },
        {
          id: uuid(),
          descricao: 'Média',
          numberOfFlavors: 2,
          numberOfSlices: 6,
        },
        {
          id: uuid(),
          descricao: 'Grande',
          numberOfFlavors: 3,
          numberOfSlices: 8,
        },
      ],
      complementos: [],
      itens_complemento: [],
    } as any);
    setTamanhosIsEnabled(false);
    setComplementosIsEnabled(false);
    resetComplementosTab();
    setGrupoPizzaId(undefined);
  }, [resetComplementosTab]);

  const toggleEditingGrupo = useCallback((id: number) => {
    setGrupoPizzaId(id);
  }, []);

  const toggleComplementosDrawer = useCallback(() => {
    setComplementosDrawerIsOpen((oldState) => !oldState);
  }, []);

  const changeCurrentComplementosTabIndex = useCallback((tabIndex: number) => {
    setCurrentComplementosTabIndex(tabIndex);
  }, []);

  const enableItensComplementos = useCallback(() => {
    setItensComplementosIsEnabled(true);
  }, []);

  const updateGrupoPizza = useCallback(async () => {
    if (grupoPizzaData.image && typeof grupoPizzaData.image !== 'string') {
      const grupoImagedata = new FormData();

      grupoImagedata.append('grupo_img', grupoPizzaData.image);

      await api.patch(`/grupo/${grupoPizzaId}`, grupoImagedata);
    }

    await api.put(`/grupo/${grupoPizzaId}`, {
      descricao: grupoPizzaData.descricao,
      pausar_venda: grupoPizzaData.pausar_venda,
    });

    // grupoPizzaData.tamanhos.forEach(async (tamanho) => {
    //   if (tamanho.isNew) {
    //     if (typeof tamanho.id === 'number') {
    //       await api.put(`/tamanho/${tamanho.id}`, {
    //         descricao: tamanho.descricao,
    //         qtd_pedacos: tamanho.numberOfSlices,
    //         ativo: true,
    //         qtd_sabores: tamanho.numberOfFlavors,
    //       });
    //     } else {
    //       await api.post('/tamanho', {
    //         empresa_id: user.empresa_id,
    //         descricao: tamanho.descricao,
    //         ativo: true,
    //         tipo: 3,
    //         qtd_sabores: tamanho.numberOfFlavors,
    //         qtd_pedacos: tamanho.numberOfSlices,
    //         grupo_id: grupoPizzaId,
    //         ordem: 1,
    //       });
    //     }
    //   }
    // });

    grupoPizzaData.itens_complemento.forEach(async (item) => {
      if (item.isNew && typeof item.id === 'number') {
        if (item.imagem && typeof item.imagem !== 'string') {
          const itemComplementoData = new FormData();

          itemComplementoData.append('item_complemento_img', item.imagem);

          await api.patch(`/complemento/item/${item.id}`, itemComplementoData);
        }
      }
    });

    const editedItensComplementos = grupoPizzaData.itens_complemento
      .filter((item) => item.isNew && typeof item.id === 'number')
      .map((item) => ({
        ...item,
        isNew: false,
      }));

    if (editedItensComplementos.length > 0) {
      await api.put('/complemento/item', {
        itens_complemento: JSON.stringify(editedItensComplementos),
      });
    }

    const newItensComplementos = grupoPizzaData.itens_complemento
      .filter(
        (item) =>
          item.isNew &&
          typeof item.id === 'string' &&
          grupoPizzaData.complementos.find(
            (complemento) => complemento.index === item.index_complemento,
          )?.id,
      )
      .map((item) => ({
        ...item,
        complemento_id: grupoPizzaData.complementos.find(
          (complemento) => complemento.index === item.index_complemento,
        )?.id,
        hasImage: !!item.imagem,
        isNew: true,
      }));

    if (newItensComplementos.length > 0) {
      const newItensComplementosData = new FormData();

      newItensComplementos
        .filter((item) => item.hasImage)
        .forEach((item) => {
          item.imagem &&
            newItensComplementosData.append(
              'item_complemento_img',
              item.imagem,
            );
        });

      newItensComplementosData.append(
        'itens_complemento',
        JSON.stringify(newItensComplementos),
      );

      await api.put('/complemento/item', newItensComplementosData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    grupoPizzaData.complementos.forEach(async (complemento) => {
      if (complemento.isNew && typeof complemento.id === 'number') {
        await api.put(`/complemento/${complemento.id}`, {
          ordem: complemento.ordem,
          descricao: complemento.descricao,
          qtd_min: complemento.qtd_min,
          qtd_max: complemento.qtd_max,
          complemento_obrigatorio: complemento.complemento_obrigatorio,
          pausar_venda: complemento.pausar_venda,
          ativo: complemento.ativo,
          tipo: 3,
        });
      }
    });

    const newComplementos = grupoPizzaData.complementos.filter(
      (complemento) => complemento.isNew && typeof complemento.id !== 'number',
    );

    if (newComplementos.length > 0) {
      const complementoData = new FormData();

      const itensComplementoArray = grupoPizzaData.itens_complemento
        .filter((item) => item.isNew && typeof item.id === 'string')
        .map((item) => {
          if (item.imagem) {
            complementoData.append('item_complemento_img', item.imagem);
          }

          return {
            codigo_complemento: item.codigo_complemento,
            descricao: item.descricao,
            tipo: item.tipo,
            nome_imagem: item.imagem ? item.imagem.name : undefined,
            produto_id: item.produto_id,
            index_complemento: item.index_complemento,
            preco: item.preco,
            qtd_min: item.qtd_min,
            qtd_max: item.qtd_max,
            empresa_id: item.empresa_id,
          };
        });

      complementoData.append(
        'itens_complemento',
        JSON.stringify(itensComplementoArray),
      );
      complementoData.append('complementos', JSON.stringify(newComplementos));
      complementoData.append('grupo_id', String(grupoPizzaId));

      await api.post('/complemento', complementoData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });
    }
  }, [grupoPizzaData, grupoPizzaId]);

  const createNewGrupoPizza = useCallback(async () => {
    const grupoData = new FormData();

    if (grupoPizzaData.image) {
      grupoData.append('grupo_img', grupoPizzaData.image);
    }

    grupoData.append('empresa_id', String(user.empresa_id));
    grupoData.append('ordem', '1');
    grupoData.append('descricao', grupoPizzaData.descricao);
    grupoData.append('tipo', '3');
    grupoData.append('ativo', 'true');

    const grupoResponse = await api.post('/grupo', grupoData);

    // grupoPizzaData.tamanhos.forEach(async (tamanho) => {
    //   await api.post('/tamanho', {
    //     empresa_id: user.empresa_id,
    //     descricao: tamanho.descricao,
    //     qtd_pedacos: tamanho.numberOfSlices,
    //     qtd_sabores: tamanho.numberOfFlavors,
    //     grupo_id: grupoResponse.data.id,
    //     tipo: 3,
    //     ativo: 'true',
    //   });
    // });

    if (grupoPizzaData.complementos.length > 0) {
      const complementoData = new FormData();

      const itensComplementoArray = grupoPizzaData.itens_complemento.map(
        (item) => {
          if (item.imagem) {
            complementoData.append('item_complemento_img', item.imagem);
          }

          return {
            codigo_complemento: item.codigo_complemento,
            descricao: item.descricao,
            tipo: item.tipo,
            nome_imagem: item.imagem ? item.imagem.name : undefined,
            produto_id: item.produto_id,
            index_complemento: item.index_complemento,
            preco: item.preco,
            qtd_min: item.qtd_min,
            qtd_max: item.qtd_max,
            empresa_id: item.empresa_id,
          };
        },
      );

      complementoData.append(
        'itens_complemento',
        JSON.stringify(itensComplementoArray),
      );
      complementoData.append(
        'complementos',
        JSON.stringify(grupoPizzaData.complementos),
      );
      complementoData.append('grupo_id', String(grupoResponse.data.id));

      await api.post('/complemento', complementoData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });
    }
  }, [user, grupoPizzaData]);

  return (
    <GrupoPizzaContext.Provider
      value={{
        grupoPizzaId,
        drawerIsOpen,
        grupoPizzaData,
        currentTabIndex,
        tamanhosIsEnabled,
        complementosIsEnabled,
        selectedComplementoIndex,
        complementosDrawerIsOpen,
        itensComplementosIsEnabled,
        currentComplementosTabIndex,
        openDrawer,
        resetDrawer,
        enableTamanhos,
        updateGrupoPizza,
        deleteComplemento,
        toggleEditingGrupo,
        enableComplementos,
        createNewGrupoPizza,
        resetComplementosTab,
        changeGrupoPizzaData,
        changeCurrentTabIndex,
        enableItensComplementos,
        toggleComplementosDrawer,
        changeSelectedComplementoIndex,
        changeCurrentComplementosTabIndex,
      }}
    >
      {children}
    </GrupoPizzaContext.Provider>
  );
};

function useGrupoPizza(): GrupoPizzaContextData {
  const context = useContext(GrupoPizzaContext);

  if (!context)
    throw new Error(
      'useGrupoPizza deve ser utilizado junto do GrupoPizzaProvider',
    );

  return context;
}

export { GrupoPizzaProvider, useGrupoPizza };
